// src/components/brand/brand-select-page.tsx
import { useMemo, useState } from "react";
import {
  Building2,
  Search,
  ChevronRight,
  RefreshCcw,
  LogOut,
  Loader2,
} from "lucide-react";
import { UpmodeLogo } from "../login/upmode-logo";
import type { BrandSummary } from "@/app/services/brand/brand-access-service";

interface BrandSelectPageProps {
  brands: BrandSummary[];
  /** Server's last-active brand — highlighted but never auto-loaded. */
  activeBrand?: string | null;
  /** Open a brand into the dashboard. Only ever called for brands with runs. */
  onSelect: (brand: string) => void | Promise<void>;
  /** Re-fetch /brands/me — an admin/onboarding run may have just landed. */
  onRetry: () => Promise<void> | void;
  onLogout: () => void;
  fullName?: string | null;
}

/**
 * Dedicated brand chooser shown to a brand-scoped user after login.
 * We do NOT auto-load a default brand — the user explicitly picks one.
 * Brands with no monitoring runs (run_count === 0) are shown but
 * disabled with a "No analysis yet" badge, so they can't be opened
 * into an empty dashboard.
 */
export function BrandSelectPage({
  brands,
  activeBrand,
  onSelect,
  onRetry,
  onLogout,
  fullName,
}: BrandSelectPageProps) {
  const [query, setQuery] = useState("");
  const [checking, setChecking] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? brands.filter((b) => b.brand_name.toLowerCase().includes(q))
      : brands;
    // Loadable brands first, then alphabetical within each group.
    return [...list].sort((a, b) => {
      const aLoadable = a.run_count > 0 ? 0 : 1;
      const bLoadable = b.run_count > 0 ? 0 : 1;
      if (aLoadable !== bLoadable) return aLoadable - bLoadable;
      return a.brand_name.localeCompare(b.brand_name);
    });
  }, [brands, query]);

  const handleRetry = async () => {
    setChecking(true);
    try {
      await onRetry();
    } finally {
      setChecking(false);
    }
  };

  const handlePick = async (brand: BrandSummary) => {
    if (brand.run_count <= 0 || opening) return;
    setOpening(brand.brand_name);
    try {
      await onSelect(brand.brand_name);
    } finally {
      setOpening(null);
    }
  };

  const showSearch = brands.length > 6;

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--background)",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {/* Logo bar — this screen has no left navigation */}
      <div
        style={{
          padding: "16px 20px",
          borderBottom: "1px solid var(--border)",
          backgroundColor: "var(--card)",
        }}
      >
        <UpmodeLogo />
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "clamp(16px, 4vh, 32px) clamp(16px, 4vw, 28px)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `
              radial-gradient(circle at 20% 20%, var(--spectral-violet-5) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, var(--signal-blue-5) 0%, transparent 50%)
            `,
            opacity: 0.5,
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            width: "100%",
            maxWidth: 540,
            position: "relative",
            zIndex: 1,
            backgroundColor: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: "clamp(28px, 4vh, 40px) clamp(20px, 3vw, 32px)",
            boxShadow: "var(--elevation-lg)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: "min(82vh, 720px)",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background:
                "linear-gradient(90deg, var(--spectral-violet), var(--signal-blue), var(--alpha-teal))",
              borderRadius: "16px 16px 0 0",
              opacity: 0.8,
            }}
          />

          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 16,
                margin: "0 auto 18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--signal-blue-10)",
                border: "1px solid var(--signal-blue-25)",
              }}
            >
              <Building2 style={{ width: 30, height: 30, color: "var(--signal-blue)" }} />
            </div>

            <h1
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-2xl)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--foreground)",
                margin: 0,
                lineHeight: "var(--leading-tight)",
                letterSpacing: "-0.01em",
              }}
            >
              Choose a brand
            </h1>

            <p
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-base)",
                color: "var(--muted-foreground)",
                lineHeight: "var(--leading-relaxed)",
                margin: "10px auto 0",
                maxWidth: 420,
              }}
            >
              {fullName ? `Welcome back, ${fullName}. ` : ""}
              Select which brand to load. Brands without an analysis yet aren't
              available to open.
            </p>
          </div>

          {showSearch && (
            <div style={{ position: "relative", margin: "20px 0 6px" }}>
              <Search
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 15,
                  height: 15,
                  color: "var(--muted-foreground)",
                  pointerEvents: "none",
                }}
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search your brands…"
                style={{
                  width: "100%",
                  padding: "10px 12px 10px 36px",
                  borderRadius: 9,
                  border: "1px solid var(--border)",
                  backgroundColor: "var(--input-background)",
                  fontSize: "var(--text-sm)",
                  outline: "none",
                  boxSizing: "border-box",
                  color: "var(--foreground)",
                }}
              />
            </div>
          )}

          {/* Brand list */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              marginTop: showSearch ? 8 : 24,
              display: "flex",
              flexDirection: "column",
              gap: 8,
              paddingRight: 2,
            }}
          >
            {filtered.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "28px 16px",
                  color: "var(--muted-foreground)",
                  fontSize: "var(--text-sm)",
                }}
              >
                No brands match your search.
              </div>
            ) : (
              filtered.map((brand) => {
                const loadable = brand.run_count > 0;
                const isActive = loadable && brand.brand_name === activeBrand;
                const isBusy = opening === brand.brand_name;
                return (
                  <button
                    key={brand.brand_name}
                    onClick={() => handlePick(brand)}
                    disabled={!loadable || !!opening}
                    aria-disabled={!loadable}
                    title={loadable ? undefined : "No analysis yet for this brand"}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "12px 14px",
                      borderRadius: 10,
                      textAlign: "left",
                      width: "100%",
                      border: `1px solid ${
                        isActive ? "var(--alpha-teal)" : "var(--border)"
                      }`,
                      backgroundColor: isActive
                        ? "var(--alpha-teal-10)"
                        : "var(--card)",
                      cursor: loadable
                        ? opening
                          ? "default"
                          : "pointer"
                        : "not-allowed",
                      opacity: loadable ? 1 : 0.55,
                      transition: "all 0.15s",
                    }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 9,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isActive
                          ? "var(--alpha-teal)"
                          : "var(--muted)",
                        color: isActive ? "white" : "var(--muted-foreground)",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {brand.brand_name.trim().slice(0, 2).toUpperCase()}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "var(--text-sm)",
                          fontWeight: isActive ? 600 : 500,
                          color: isActive
                            ? "var(--alpha-teal)"
                            : "var(--foreground)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {brand.brand_name}
                      </div>
                      <div
                        style={{
                          fontSize: "var(--text-xs)",
                          color: "var(--muted-foreground)",
                          marginTop: 2,
                        }}
                      >
                        {loadable
                          ? `${brand.run_count} ${
                              brand.run_count === 1 ? "analysis" : "analyses"
                            }`
                          : "No analysis yet"}
                      </div>
                    </div>

                    {loadable &&
                      (isBusy ? (
                        <Loader2
                          className="animate-spin"
                          style={{ width: 16, height: 16, color: "var(--alpha-teal)" }}
                        />
                      ) : (
                        <ChevronRight
                          style={{
                            width: 18,
                            height: 18,
                            color: "var(--muted-foreground)",
                            flexShrink: 0,
                          }}
                        />
                      ))}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer actions */}
          <div
            style={{
              marginTop: 18,
              paddingTop: 16,
              borderTop: "1px solid var(--border)",
              display: "flex",
              gap: 10,
            }}
          >
            <button
              onClick={handleRetry}
              disabled={checking}
              style={{
                flex: 1,
                height: 42,
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-weight-medium)",
                background: "transparent",
                color: "var(--muted-foreground)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius)",
                cursor: checking ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              {checking ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: 15, height: 15 }} />
                  Checking…
                </>
              ) : (
                <>
                  <RefreshCcw style={{ width: 15, height: 15 }} />
                  Refresh
                </>
              )}
            </button>

            <button
              onClick={onLogout}
              style={{
                flex: 1,
                height: 42,
                fontFamily: "var(--font-sans)",
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-weight-medium)",
                background: "transparent",
                color: "var(--muted-foreground)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <LogOut style={{ width: 15, height: 15 }} />
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
